/**
 * ticket-inapp.editorial — one of the package's two shipped in-app card
 * variants (see ticket-inapp.card.ts for the variant dispatcher). An
 * asymmetric two-column split: a narrow inverted spine of dense facts
 * (ticket type, id, brand) runs the full card height on the
 * left; a wide, left-aligned identity + credential column — event name,
 * schedule, venue, the QR hero — sits on the right, deliberately never
 * centered. The QR stays left-aligned with its column rather than
 * centered, carrying the asymmetry through to the hero itself. Mounts the
 * real QR hero (ticket-inapp.qr-hero.ts) — see that file's header for the
 * hybrid-palette rationale.
 *
 * `min-w-0` on both grid items (not just `minmax(0, …)` on the tracks) is
 * load-bearing: a grid item's default `min-width: auto` resolves to its
 * min-content size regardless of the track's own `minmax(0, …)`, so
 * without it the fixed-size QR plate forces the whole card wider than the
 * viewport at a real phone width instead of wrapping inside its own
 * column.
 *
 * ONE COMPOSITION AT EVERY SIZE. The card carries no breakpoint and no
 * container query of its own: a ticket is an identity document, and a
 * buyer's ticket must be the same object on a 320px phone as on a desktop,
 * not a different arrangement of the same facts. Every consumer therefore
 * lays this card out at its native 384px design width and scales the whole
 * rendering to fit — `renderTicketCardScaled`
 * (ticket-inapp.scaled-card.ts) for the on-screen surfaces,
 * `TICKET_FILE_DESIGN_WIDTH` for the rasterized file — so what a narrow
 * host changes is the card's on-screen SIZE, never its layout.
 *
 * "The QR on the ticket number's eye level" is what the two columns buy:
 * both subgrid onto the SAME three rows, so the credential and the id share
 * one row line and align by construction rather than by a tuned spacer that
 * a longer event name or a German compound would undo. Every number in the
 * stylesheet below was measured on the real `order-access` surface, not
 * reasoned about; the comments name what each one was measured against.
 *
 * The stylesheet lives in a co-located `<style>` block rather than in
 * Tailwind utilities because an arbitrary utility is only emitted where the
 * Tailwind pipeline scans THIS package's source, so it renders inert in a
 * consumer — the admin gallery/picker shadow root, any app importing the
 * card — while a self-contained `<style>` ships with the card and resolves
 * identically everywhere.
 *
 * ONE COMPOSITION UNDER EVERY SCHEME, for the same reason there is one under
 * every size: both columns paint a pinned ground (`tkt-ink-ground` /
 * `tkt-paper-ground`, ticket-inapp.palette.ts) rather than a theme surface
 * family, so the dark spine stays dark and the paper identity column stays
 * paper on a light host as on a dark one — and the QR's black modules keep
 * the light ground they are legible against. Neither column is a
 * `<schmancy-surface>`: that primitive's whole contract is tonal depth read
 * from the ambient theme, which is precisely what a document must not do, and
 * its translucent fill would additionally let the host page bleed through the
 * spine. A hairline `border-outlineVariant` rides the outer card only (never
 * the internal seam) — that edge separates card from page, so it is the one
 * that SHOULD track the host, and the `outline` family reads correctly
 * against a dark or a light one.
 */

import { formatDateRange } from "@mhmo91/rx";
import { translate } from "@momo/i18n";
import { html, type TemplateResult } from "lit";
import type { TicketTemplateProps } from "./ticket.props.ts";
import { ticketCardGrounds } from "./ticket-inapp.palette.ts";
import { renderQrCredentialHero } from "./ticket-inapp.qr-hero.ts";
import { ticketCardStyles } from "./ticket-inapp.styles.ts";

export function renderTicketInAppEditorial(
	props: TicketTemplateProps,
): TemplateResult {
	const { locale, brand, event, ticket } = props;
	const ticketTypeLabel = translate(locale, "documents.ticket.ticketTypeLabel");
	const scheduleValue = formatDateRange(event.start, event.end, {
		locale: locale === "de" ? "de-DE" : "en-US",
		month: "long",
		timeZone: event.timezone,
	});

	return html`
    ${ticketCardGrounds}${ticketCardStyles}
    <style>
      .tkt-ed-grid {
        display: grid;
        grid-template-columns: minmax(0, 0.72fr) minmax(0, 2.28fr);
        /* Three shared rows — facts, credential, signature — the middle one
           absorbing every spare pixel. */
        grid-template-rows: auto minmax(0, 1fr) auto;
        /* The code carries no quiet zone of its own, so this gap IS the
           quiet zone above it (ticket-inapp.qr-hero.ts) as well as the
           composition's rhythm — hence one step above the 1.5rem used
           within a group. */
        row-gap: 2rem;
      }
      /* Both columns subgrid onto those SAME three rows, which is what puts
         the QR's top edge exactly on the ticket number's: the two sit in one
         shared row and align by grid line, not by a hand-tuned spacer that a
         longer event name or a German compound would undo. Each column still
         paints its own full-height background because it spans all three
         rows as one item — the row gap runs inside it, not between two
         separately-painted boxes. */
      .tkt-ed-col { display: grid; grid-row: span 3; grid-template-rows: subgrid; }
      /* Spanning the last two rows (not just the middle one) lets the QR
         spend the signature row's height too, while a start self-alignment
         keeps its top pinned to the shared line rather than centering it in
         the taller area. */
      .tkt-ed-qr {
        display: block;
        min-height: 0;
        grid-row: 2 / span 2;
        align-self: start;
      }
      .tkt-ed-qr .tkt-qr-stack { min-height: 0; gap: 0.75rem; }
      /* THE ALIGNED THING IS THE INK, and with no white plate and no baked
         quiet zone (ticket-inapp.qr-hero.ts) the code's box IS its ink box —
         so it needs no compensation at all. Its top edge is row 2's own
         line, the same line the ticket number sits on; its left edge is the
         identity column's content edge, the same edge the event name, date
         and venue start from. Both alignments are the layout's, not a
         correction applied on top of it. */
      .tkt-ed-qr .tkt-qr-fill { flex: none; width: 100%; min-height: 0; }
      /* Bounded, so an over-long identity block spends its own box rather
         than pushing the credential below the card's edge. */
      .tkt-ed-text { min-height: 0; }
      /* The two columns' padding-block must stay EQUAL, which subgrid row
         alignment depends on: unequal vertical padding between two
         subgridded columns offsets their shared row lines and undoes the
         QR/ticket-number alignment. align-items:stretch, not center — center
         would centre each fact group inside its own subgrid row, and the
         tall middle row would then drop the ticket number ~136px below the
         QR it is supposed to line up with. */
      .tkt-ed-spine {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        min-width: 0;
        align-items: stretch;
        gap: 0.75rem;
        padding-block: 1.5rem;
        padding-inline: 0.75rem;
      }
      .tkt-ed-identity {
        display: flex;
        flex-direction: column;
        min-width: 0;
        min-height: 0;
        padding-block: 1.5rem;
        padding-inline: 1rem;
        gap: 1.5rem;
      }
      .tkt-ed-text { display: flex; flex-direction: column; gap: 1rem; }
      /* Each fact group must be allowed to reach its wrapped width: a grid
         item defaults to min-width:auto (its min-CONTENT size), so the brand
         would otherwise be sheared mid-word by the card's overflow-hidden.
         With min-width:0 it wraps to two lines inside the spine instead. */
      .tkt-ed-spine > * { min-width: 0; }
      /* The spine's ticket-type line and the identity column's event name are
         the two long, potentially unbreakable tokens on this card (a German
         compound like "Ermaessigungsticket"; a run-on event name). The break
         is shipped here as REAL inherited CSS, not a Tailwind break-words /
         wrap-break-word utility, for the SAME reason the whole stylesheet is:
         an arbitrary utility used only in THIS package is never emitted into a
         consumer app's Tailwind output, so it renders inert there and the
         word's glyphs ink-overflow their column — in the narrow spine's case,
         sliding under the dark panel. overflow-wrap is an inherited property,
         so setting it on the host reaches schmancy-typography's own shadow
         text; "anywhere" (not "break-word") also lets the grid item reach its
         true min-content width. */
      .tkt-ed-wrap { overflow-wrap: anywhere; }
    </style>
    <div class="tkt-card tkt-ed-card">
      <div class="tkt-ed-grid tkt-ed-fill">
        <div class="tkt-ink-ground tkt-ed-col tkt-ed-spine">
          <div class="tkt-ed-stack">
            <schmancy-typography type="label" token="xs" transform="uppercase" class="tkt-track-widest tkt-dim">
              ${ticketTypeLabel}
            </schmancy-typography>
            <schmancy-typography type="label" token="sm" class="tkt-ed-wrap">${ticket.typeName}</schmancy-typography>
          </div>

          <schmancy-typography type="label" token="sm" class="tkt-mono tkt-dim">
            ${ticket.displayId}
          </schmancy-typography>

          <schmancy-typography type="label" token="xs" transform="uppercase" class="tkt-track-widest tkt-dim">
            ${brand.name}
          </schmancy-typography>
        </div>

        <div class="tkt-paper-ground tkt-ed-col tkt-ed-identity">
          <!-- Event name and its schedule/venue stay ONE row-1 child, not two
               siblings: the row they share is the counterweight to the spine's
               ticket-type row, and splitting them would push the QR down into
               row 3 and off the ticket number's line. -->
          <div class="tkt-ed-text">
            <schmancy-typography
              type="display"
              token=${event.name.length > 42 ? "xs" : "sm"}
              weight="bold"
              maxLines="2"
              class="tkt-ed-wrap tkt-ed-title"
            >
              ${event.name}
            </schmancy-typography>

            <div class="tkt-ed-stack">
              <schmancy-typography type="title" token="sm">${scheduleValue}</schmancy-typography>
              <schmancy-typography type="body" token="sm" class="tkt-dim">
                ${event.venue.name} — ${event.venue.address}
              </schmancy-typography>
            </div>
          </div>

          <!-- "fill", not a pixel size: the QR takes the whole width its
               column has left and is capped by the height the shared rows
               leave it, so the credential grows with the card instead of
               sitting at a number chosen once. No pt-2 — the grid's own
               row-gap owns that distance (GAP_OWNS_INTER_ELEMENT_SPACING). -->
          <div class="tkt-ed-qr">${renderQrCredentialHero(locale, ticket.qrToken, "fill", "start")}</div>
        </div>
      </div>
    </div>
  `;
}
